import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Smartphone } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_be106047-7bc1-4755-b814-a58a16500b58/artifacts/p2la4lzy_ChatGPT%20Image%20Dec%2028%2C%202025%2C%2009_16_43%20PM.png";

// Session key — popup re-appears every new login/session until app is installed
const SESSION_DISMISS_KEY = "pwa_dismissed_this_session";

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const PWAInstallPopup = () => {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  // Capture install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installedHandler = () => {
      setInstalled(true);
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  // Show popup when a user logs in (session-scoped)
  useEffect(() => {
    if (!user || installed) {
      setVisible(false);
      return;
    }
    const dismissed = sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
    if (dismissed) return;
    // Slight delay so it appears after route transition
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [user, installed]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // No native prompt available (iOS Safari or already dismissed by browser)
      // Keep popup visible with manual instructions
      return;
    }
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setInstalled(true);
        setVisible(false);
      }
    } catch (e) {
      // ignore
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setVisible(false);
  };

  if (installed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ type: "spring", damping: 22, stiffness: 260 }}
          className="fixed bottom-4 right-4 z-[9999] w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-2xl shadow-2xl border border-emerald-100 overflow-hidden"
          data-testid="pwa-install-popup"
        >
          <div className="relative p-5">
            <button
              onClick={handleDismiss}
              aria-label="Close"
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
              data-testid="pwa-install-close-btn"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-md">
                <img
                  src={LOGO_URL}
                  alt="Happy Holidays DMC"
                  className="w-8 h-8 rounded"
                />
              </div>
              <div className="flex-1 min-w-0 pr-4">
                <h3 className="text-base font-semibold text-gray-900">
                  Install Happy Holidays DMC
                </h3>
                <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                  Install Happy Holidays DMC for quick access, offline support,
                  and a faster, app-like experience.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Button
                onClick={handleInstall}
                className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                data-testid="pwa-install-btn"
              >
                {deferredPrompt ? (
                  <>
                    <Download className="w-4 h-4" />
                    Install App
                  </>
                ) : (
                  <>
                    <Smartphone className="w-4 h-4" />
                    How to Install
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleDismiss}
                className="h-9 px-3 text-sm rounded-lg border-gray-200"
                data-testid="pwa-install-dismiss-btn"
              >
                Not now
              </Button>
            </div>

            {!deferredPrompt && (
              <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
                On iOS: tap <span className="font-semibold">Share</span> →{" "}
                <span className="font-semibold">Add to Home Screen</span>. On
                desktop Chrome: tap the install icon in the address bar.
              </p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPopup;
